/**
 * Users Management Page - Manage user roles and divisions
 */

import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import adminApi from '../api/adminApi'
import type { AdminUserListItem, MicrosoftSyncStats, AuditLogEntry } from '../types/admin.types'
import LoadingPage from '@/shared/components/LoadingPage'
import { useAuth } from '@/core/auth/AuthContext'

const UsersPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AdminUserListItem | null>(null)
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [showDivisionModal, setShowDivisionModal] = useState(false)
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [showAuditModal, setShowAuditModal] = useState(false)
  const [auditPage, setAuditPage] = useState(1)
  const [profileForm, setProfileForm] = useState({
    job_title: '', department: '', first_name: '', last_name: '', display_name: '',
  })
  const [syncStats, setSyncStats] = useState<MicrosoftSyncStats | null>(null)

  // Check if user is admin
  const isAdmin = user?.roles?.some(role => role.name === 'admin')

  // Redirect non-admin users to main page
  useEffect(() => {
    if (user && !isAdmin) {
      toast.error('You do not have permission to access this page')
      navigate('/')
    }
  }, [user, isAdmin, navigate])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Fetch users
  const { data, isLoading } = useQuery({
    queryKey: ['adminUsers', { search: searchTerm, page, includeInactive }],
    queryFn: () => adminApi.getUsers({ search: searchTerm, page, limit: 20, include_inactive: includeInactive }),
    placeholderData: keepPreviousData,
  })

  // Fetch roles — rarely changes, cache for 10 minutes
  const { data: rolesData } = useQuery({
    queryKey: ['adminRoles'],
    queryFn: adminApi.getRoles,
    staleTime: 10 * 60 * 1000,
  })

  // Fetch divisions — rarely changes, cache for 10 minutes
  const { data: divisionsData } = useQuery({
    queryKey: ['adminDivisions'],
    queryFn: () => adminApi.getDivisions(),
    staleTime: 10 * 60 * 1000,
  })

  const roles = rolesData?.roles || []
  const divisions = divisionsData?.divisions || []

  // Mutations
  const assignRoleMutation = useMutation({
    mutationFn: ({ userId, roleName }: { userId: string; roleName: string }) =>
      adminApi.assignRole(userId, { role_name: roleName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] })
      toast.success('Role assigned successfully')
      setShowRoleModal(false)
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to assign role')
    },
  })

  const removeRoleMutation = useMutation({
    mutationFn: ({ userId, roleName }: { userId: string; roleName: string }) =>
      adminApi.removeRole(userId, roleName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] })
      toast.success('Role removed successfully')
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to remove role')
    },
  })

  const updateDivisionMutation = useMutation({
    mutationFn: ({ userId, divisionId }: { userId: string; divisionId: string | null }) =>
      adminApi.updateUserDivision(userId, { division_id: divisionId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] })
      toast.success('Division updated successfully')
      setShowDivisionModal(false)
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to update division')
    },
  })

  const syncMicrosoftMutation = useMutation({
    mutationFn: () => adminApi.syncMicrosoftUsers({ sync_managers: true }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] })
      setSyncStats(data.stats)
      setShowSyncModal(true)
      toast.success(data.message)
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to sync users from Microsoft 365')
    },
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      adminApi.updateUserStatus(userId, isActive),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] })
      toast.success(data.message)
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to update user status')
    },
  })

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => adminApi.deleteUser(userId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] })
      toast.success(data.message)
      setShowDeleteModal(false)
      setSelectedUser(null)
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to delete user')
    },
  })

  const updateProfileMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: typeof profileForm }) =>
      adminApi.updateUserProfile(userId, {
        job_title: data.job_title || undefined,
        department: data.department || undefined,
        first_name: data.first_name || undefined,
        last_name: data.last_name || undefined,
        display_name: data.display_name || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] })
      toast.success('Profile updated and locked from Azure AD sync')
      setShowProfileModal(false)
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to update profile')
    },
  })

  const resetProfileMutation = useMutation({
    mutationFn: (userId: string) => adminApi.resetProfileToAzure(userId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] })
      toast.success(data.message)
      setShowProfileModal(false)
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to reset profile')
    },
  })

  const { data: auditData, isLoading: auditLoading } = useQuery({
    queryKey: ['userAuditLog', selectedUser?.id, auditPage],
    queryFn: () => adminApi.getUserAuditLog(selectedUser!.id, { page: auditPage, limit: 10 }),
    enabled: showAuditModal && !!selectedUser,
  })

  // Fetch full user data when profile modal opens — list cache may not have all fields
  const { data: profileUserData, isLoading: profileUserLoading } = useQuery({
    queryKey: ['adminUser', selectedUser?.id],
    queryFn: () => adminApi.getUser(selectedUser!.id),
    enabled: showProfileModal && !!selectedUser,
    staleTime: 0,
  })

  // Sync form fields when fresh user data arrives
  useEffect(() => {
    if (!profileUserData?.user) return
    const u = profileUserData.user as any
    setProfileForm({
      first_name: u.first_name || '',
      last_name: u.last_name || '',
      display_name: u.display_name || '',
      job_title: u.job_title || '',
      department: u.department || '',
    })
  }, [profileUserData])

  const handleAssignRole = (roleName: string) => {
    if (selectedUser) {
      assignRoleMutation.mutate({ userId: selectedUser.id, roleName })
    }
  }

  const handleRemoveRole = (targetUser: AdminUserListItem, roleName: string) => {
    if (confirm(`Remove "${roleName}" role from ${targetUser.full_name}?`)) {
      removeRoleMutation.mutate({ userId: targetUser.id, roleName })
    }
  }

  const handleUpdateDivision = (divisionId: string | null) => {
    if (selectedUser) {
      updateDivisionMutation.mutate({ userId: selectedUser.id, divisionId })
    }
  }

  const getRoleBadgeStyle = (roleName: string) => {
    switch (roleName) {
      case 'admin':
        return 'bg-red-100 text-red-700 border border-red-200'
      case 'manager':
        return 'bg-red-100 text-red-700 border border-red-200'
      case 'hr':
        return 'bg-rose-100 text-rose-700 border border-rose-200'
      default:
        return 'bg-gray-100 text-gray-700 border border-gray-200'
    }
  }

  const getDivisionBadgeColor = (divisionName: string) => {
    const name = divisionName?.toLowerCase()
    switch (name) {
      case 'human resources':
      case 'hr':
        return 'bg-rose-50 text-rose-700 border border-rose-200'
      case 'finance':
        return 'bg-emerald-50 text-emerald-700 border border-emerald-200'
      case 'engineering':
        return 'bg-red-50 text-red-700 border border-red-200'
      case 'operations':
        return 'bg-orange-50 text-orange-700 border border-orange-200'
      case 'sales':
        return 'bg-amber-50 text-amber-700 border border-amber-200'
      case 'marketing':
        return 'bg-pink-50 text-pink-700 border border-pink-200'
      default:
        return 'bg-slate-50 text-slate-700 border border-slate-200'
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-blue-500',
      'bg-emerald-500',
      'bg-purple-500',
      'bg-amber-500',
      'bg-rose-500',
      'bg-cyan-500',
      'bg-red-500',
      'bg-teal-500',
    ]
    const index = name.charCodeAt(0) % colors.length
    return colors[index]
  }

  // Show loading while checking auth or redirecting
  if (!user || !isAdmin || isLoading) return <LoadingPage />

  const users = data?.data || []
  const pagination = data?.pagination

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-red-200 mb-4">
            <Link to="/admin" className="hover:text-white transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </Link>
            <span>/</span>
            <span className="text-white">Users</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                User Management
              </h1>
              <p className="mt-2 text-red-200">
                Manage user accounts, roles, and division assignments
              </p>
            </div>

            <button
              onClick={() => syncMicrosoftMutation.mutate()}
              disabled={syncMicrosoftMutation.isPending}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-red-700 rounded-lg font-medium shadow-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {syncMicrosoftMutation.isPending ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Syncing...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Sync Microsoft 365
                </>
              )}
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{pagination?.total || 0}</p>
                  <p className="text-sm text-red-200">Total Users</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-400/30 rounded-lg">
                  <svg className="w-5 h-5 text-emerald-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{users.filter((u: AdminUserListItem) => u.is_active).length}</p>
                  <p className="text-sm text-red-200">Active</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-400/30 rounded-lg">
                  <svg className="w-5 h-5 text-amber-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{users.filter((u: AdminUserListItem) => !u.is_active).length}</p>
                  <p className="text-sm text-red-200">Inactive</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-rose-400/30 rounded-lg">
                  <svg className="w-5 h-5 text-rose-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{divisions.length}</p>
                  <p className="text-sm text-red-200">Divisions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search & Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by name, email, or job title..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer px-4 py-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => {
                  setIncludeInactive(e.target.checked)
                  setPage(1)
                }}
                className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700 font-medium">Show inactive users</span>
            </label>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Division
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Roles
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <p className="text-gray-500 font-medium">No users found</p>
                        <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filters</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  users.map((userRow) => (
                    <tr
                      key={userRow.id}
                      className={`hover:bg-gray-50 transition-colors ${!userRow.is_active ? 'bg-gray-50/50' : ''}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm ${getAvatarColor(userRow.full_name || '')} ${!userRow.is_active ? 'opacity-50' : ''}`}>
                            {getInitials(userRow.full_name || '')}
                          </div>
                          <div className={!userRow.is_active ? 'opacity-60' : ''}>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900">{userRow.full_name}</p>
                              {userRow.profile_manually_managed && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-700 border border-amber-200" title="Profile is manually managed — Azure AD sync is paused for this user">
                                  Manual
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{userRow.email}</p>
                            {userRow.job_title && (
                              <p className="text-xs text-gray-400 mt-0.5">{userRow.job_title}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {userRow.division_name ? (
                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${getDivisionBadgeColor(userRow.division_name)}`}>
                              {userRow.division_name}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400 italic">Unassigned</span>
                          )}
                          <button
                            onClick={() => {
                              setSelectedUser(userRow)
                              setShowDivisionModal(true)
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Change Division"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1.5">
                          {userRow.roles.map((role) => (
                            <span
                              key={role.id}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${getRoleBadgeStyle(role.name)}`}
                            >
                              {role.name}
                              <button
                                onClick={() => handleRemoveRole(userRow, role.name)}
                                className="hover:text-red-600 transition-colors"
                                title="Remove role"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </span>
                          ))}
                          <button
                            onClick={() => {
                              setSelectedUser(userRow)
                              setShowRoleModal(true)
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-full transition-colors"
                            title="Add Role"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {userRow.is_active ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedUser(userRow)
                              setProfileForm({ job_title: '', department: '', first_name: '', last_name: '', display_name: '' })
                              setShowProfileModal(true)
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit profile"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(userRow)
                              setAuditPage(1)
                              setShowAuditModal(true)
                            }}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View audit log"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                          </button>
                          {userRow.id !== user?.id && (
                            <>
                              <button
                                onClick={() => updateStatusMutation.mutate({ userId: userRow.id, isActive: !userRow.is_active })}
                                disabled={updateStatusMutation.isPending}
                                className={`p-2 rounded-lg transition-colors ${
                                  userRow.is_active
                                    ? 'text-amber-600 hover:bg-amber-50'
                                    : 'text-emerald-600 hover:bg-emerald-50'
                                }`}
                                title={userRow.is_active ? 'Deactivate user' : 'Activate user'}
                              >
                                {userRow.is_active ? (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                  </svg>
                                ) : (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedUser(userRow)
                                  setShowDeleteModal(true)
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete user"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                          {userRow.id === user?.id && (
                            <span className="text-xs text-gray-400 italic px-2">You</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-600">
                Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                <span className="font-medium">{pagination.pages}</span>
                {' '}({pagination.total} total users)
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="inline-flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.pages}
                  className="inline-flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Role Assignment Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-xl">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Assign Role</h3>
                  <p className="text-sm text-gray-500">{selectedUser.full_name}</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-2">
              {roles
                .filter((role) => ['admin', 'manager', 'user', 'hr'].includes(role.name.toLowerCase()))
                .filter((role) => !selectedUser.roles.some((r) => r.name === role.name))
                .map((role) => (
                  <button
                    key={role.id}
                    onClick={() => handleAssignRole(role.name)}
                    disabled={assignRoleMutation.isPending}
                    className="w-full text-left px-4 py-3 border border-gray-200 rounded-xl hover:bg-red-50 hover:border-red-300 disabled:opacity-50 transition-all group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 capitalize group-hover:text-red-700">{role.name}</p>
                        {role.description && (
                          <p className="text-sm text-gray-500">{role.description}</p>
                        )}
                      </div>
                      <svg className="w-5 h-5 text-gray-400 group-hover:text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                  </button>
                ))}
              {roles
                .filter((role) => ['admin', 'manager', 'user', 'hr'].includes(role.name.toLowerCase()))
                .filter((role) => !selectedUser.roles.some((r) => r.name === role.name)).length === 0 && (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-500">User has all available roles</p>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowRoleModal(false)}
                className="w-full px-4 py-2.5 text-gray-700 font-medium hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Division Assignment Modal */}
      {showDivisionModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-rose-100 rounded-xl">
                  <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Assign Division</h3>
                  <p className="text-sm text-gray-500">{selectedUser.full_name}</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-2 max-h-96 overflow-y-auto">
              <button
                onClick={() => handleUpdateDivision(null)}
                disabled={updateDivisionMutation.isPending}
                className={`w-full text-left px-4 py-3 border rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all ${
                  !selectedUser.division_id ? 'border-red-500 bg-red-50 ring-2 ring-red-200' : 'border-gray-200'
                }`}
              >
                <p className="font-medium text-gray-500">No Division</p>
              </button>
              {divisions.map((division) => {
                const isSpecial = ['HR', 'FIN'].includes(division.code)
                return (
                  <button
                    key={division.id}
                    onClick={() => handleUpdateDivision(division.id)}
                    disabled={updateDivisionMutation.isPending}
                    className={`w-full text-left px-4 py-3 border rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all ${
                      selectedUser.division_id === division.id ? 'border-red-500 bg-red-50 ring-2 ring-red-200' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{division.name}</p>
                        <p className="text-sm text-gray-500">{division.code}</p>
                      </div>
                      {isSpecial && (
                        <span className={`text-xs px-2 py-1 rounded-full ${getDivisionBadgeColor(division.name)}`}>
                          {division.code === 'HR' ? 'HR Access' : 'Finance Access'}
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
            <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowDivisionModal(false)}
                className="w-full px-4 py-2.5 text-gray-700 font-medium hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Microsoft Sync Results Modal */}
      {showSyncModal && syncStats && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Sync Complete</h3>
                  <p className="text-sm text-gray-500">Microsoft 365 users synchronized</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900">{syncStats.total_fetched}</p>
                  <p className="text-sm text-gray-500">Fetched</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">+{syncStats.created}</p>
                  <p className="text-sm text-gray-500">Created</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{syncStats.updated}</p>
                  <p className="text-sm text-gray-500">Updated</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-400">{syncStats.skipped}</p>
                  <p className="text-sm text-gray-500">Skipped</p>
                </div>
              </div>
              {syncStats.restored > 0 && (
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{syncStats.restored}</p>
                  <p className="text-sm text-blue-500">Restored</p>
                </div>
              )}
              {syncStats.errors > 0 && (
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{syncStats.errors}</p>
                  <p className="text-sm text-red-500">Errors</p>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => {
                  setShowSyncModal(false)
                  setSyncStats(null)
                }}
                className="w-full px-4 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showProfileModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-xl">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Edit Profile</h3>
                    <p className="text-sm text-gray-500">{selectedUser.full_name}</p>
                  </div>
                </div>
                {selectedUser.profile_manually_managed && (
                  <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full border border-amber-200">
                    Manually managed
                  </span>
                )}
              </div>
            </div>
            <div className="p-6 space-y-4">
              {profileUserLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-600" />
                </div>
              )}
              {!profileUserLoading && (
              <><div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-800">
                Saving will lock this user's profile from Azure AD sync. Only email (login identity) continues to sync.
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={profileForm.first_name}
                    onChange={(e) => setProfileForm((f) => ({ ...f, first_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={profileForm.last_name}
                    onChange={(e) => setProfileForm((f) => ({ ...f, last_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                <input
                  type="text"
                  value={profileForm.display_name}
                  onChange={(e) => setProfileForm((f) => ({ ...f, display_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                <input
                  type="text"
                  placeholder={selectedUser.job_title || 'e.g. Software Engineer'}
                  value={profileForm.job_title}
                  onChange={(e) => setProfileForm((f) => ({ ...f, job_title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <select
                  value={profileForm.department}
                  onChange={(e) => setProfileForm((f) => ({ ...f, department: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="">— Select department —</option>
                  {divisions.filter((d) => d.is_active).map((d) => (
                    <option key={d.id} value={d.name}>{d.name}</option>
                  ))}
                </select>
              </div>
              </>)}
            </div>
            <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex gap-3 flex-wrap">
              {selectedUser.profile_manually_managed && (
                <button
                  onClick={() => {
                    if (confirm('Remove the manual lock? Azure AD will overwrite this profile on next sync.')) {
                      resetProfileMutation.mutate(selectedUser.id)
                    }
                  }}
                  disabled={resetProfileMutation.isPending}
                  className="px-4 py-2.5 text-sm text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  Reset to Azure AD
                </button>
              )}
              <button
                onClick={() => setShowProfileModal(false)}
                className="px-4 py-2.5 text-gray-700 font-medium hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => updateProfileMutation.mutate({ userId: selectedUser.id, data: profileForm })}
                disabled={updateProfileMutation.isPending || profileUserLoading}
                className="ml-auto px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {updateProfileMutation.isPending ? 'Saving...' : 'Save & Lock'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Modal */}
      {showAuditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-xl">
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Audit Log</h3>
                  <p className="text-sm text-gray-500">{selectedUser.full_name}</p>
                </div>
              </div>
              <button onClick={() => setShowAuditModal(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {auditLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-600" />
                </div>
              ) : (auditData?.audit_log ?? []).length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p>No audit entries yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(auditData?.audit_log ?? []).map((entry: AuditLogEntry) => (
                    <div key={entry.id} className="flex gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-500" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <span className="font-medium text-gray-900 text-sm capitalize">
                              {entry.action.replace(/_/g, ' ')}
                            </span>
                            {entry.actor_name && (
                              <span className="text-xs text-gray-500 ml-2">by {entry.actor_name}</span>
                            )}
                          </div>
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {new Date(entry.created_at).toLocaleString()}
                          </span>
                        </div>
                        {entry.new_values && Object.keys(entry.new_values).length > 0 && (
                          <div className="mt-2 space-y-1">
                            {Object.entries(entry.new_values).map(([key, val]) => (
                              <div key={key} className="text-xs flex gap-2">
                                <span className="text-gray-400 capitalize">{key.replace(/_/g, ' ')}:</span>
                                <span className="text-gray-700 font-medium truncate">{String(val ?? '—')}</span>
                                {entry.old_values?.[key] !== undefined && (
                                  <span className="text-gray-400 line-through truncate">{String(entry.old_values[key] ?? '—')}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {entry.ip_address && (
                          <p className="text-xs text-gray-400 mt-1">{entry.ip_address}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {auditData?.pagination && auditData.pagination.pages > 1 && (
              <div className="p-4 border-t border-gray-100 flex items-center justify-between">
                <button
                  onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                  disabled={auditPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {auditPage} of {auditData.pagination.pages}
                </span>
                <button
                  onClick={() => setAuditPage((p) => Math.min(auditData.pagination.pages, p + 1))}
                  disabled={auditPage === auditData.pagination.pages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-xl">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-medium ${getAvatarColor(selectedUser.full_name || '')}`}>
                  {getInitials(selectedUser.full_name || '')}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{selectedUser.full_name}</p>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex gap-3">
                  <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm text-amber-800">
                    This will remove the user from HRMS. Their Azure AD account will remain active.
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setSelectedUser(null)
                }}
                className="flex-1 px-4 py-2.5 text-gray-700 font-medium hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteUserMutation.mutate(selectedUser.id)}
                disabled={deleteUserMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleteUserMutation.isPending ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UsersPage
